import React from 'react';
import { formatCents } from '../lib/money';

/**
 * One subscription's worth of plan + add-ons + initial fee. A single order
 * submission can include more than one of these (see spec: "create a new
 * customer and subscription or multiple subscriptions for the customer") --
 * each becomes its own Stripe Subscription + pest_orders row.
 *
 * addedAsExtra controls the plan dropdown: a block added via "+ Add
 * another subscription" always shows the full catalog (common +
 * specialized); a block that was there from the start defaults to only
 * "featured" (common) plans so the list doesn't sprawl across dozens of
 * specialized ones a CSR rarely needs. A CSR who needs only a specialized
 * plan adds a second block, picks it there, then removes the now-unused
 * first block.
 *
 * That removal is exactly why this can't just be "is this the first block
 * in the array": once the original block is removed, the specialized one
 * shifts into position 0, and array position alone would wrongly revert
 * it to the featured-only list -- which doesn't contain the plan it has
 * selected, making the dropdown appear to lose that selection. So once a
 * block's own selected plan is itself non-featured, it keeps showing the
 * full catalog regardless of where it ends up in the array.
 */
export default function SubscriptionBuilder({ products, value, onChange, onRemove, canRemove, addedAsExtra, hideFees }) {
  const allPlans = products.filter((p) => p.category === 'plan');
  const selectedPlanIsSpecialized = value.priceId && allPlans.some((p) => p.stripePriceId === value.priceId && !p.featured);
  const showAllPlans = addedAsExtra || selectedPlanIsSpecialized;
  const plans = showAllPlans ? allPlans : allPlans.filter((p) => p.featured);
  const addons = products.filter((p) => p.category === 'addon');
  const fees = products.filter((p) => p.category === 'fee');
  // A plan's required fee (if any) is already shown/auto-applied below --
  // don't also offer it in the optional-extras list, or a CSR could select
  // it a second time (the server would dedupe it, but showing it twice is
  // just confusing).
  const extraFeeOptions = fees.filter((f) => f.stripePriceId !== value.initialFeePriceId);

  const selectedPlan = allPlans.find((p) => p.stripePriceId === value.priceId);

  function toggleAddon(priceId) {
    const set = new Set(value.addonPriceIds);
    if (set.has(priceId)) set.delete(priceId);
    else set.add(priceId);
    onChange({ ...value, addonPriceIds: Array.from(set) });
  }

  function toggleExtraFee(priceId) {
    const set = new Set(value.extraFeePriceIds);
    if (set.has(priceId)) set.delete(priceId);
    else set.add(priceId);
    onChange({ ...value, extraFeePriceIds: Array.from(set) });
  }

  const addonTotal = value.addonPriceIds.reduce((sum, id) => {
    const p = addons.find((a) => a.stripePriceId === id);
    return sum + (p ? p.unitAmountCents : 0);
  }, 0);
  const monthlyTotal = (selectedPlan ? selectedPlan.unitAmountCents : 0) + addonTotal;

  const extraFeeTotal = value.extraFeePriceIds.reduce((sum, id) => {
    const f = fees.find((x) => x.stripePriceId === id);
    return sum + (f ? f.unitAmountCents : 0);
  }, 0);
  const oneTimeTotal = value.initialFeeCents + extraFeeTotal;

  return (
    <div className="card" style={{ background: '#fbfbfc' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <label>Plan</label>
        {canRemove && (
          <a href="#" onClick={(e) => { e.preventDefault(); onRemove(); }} style={{ fontSize: 13 }}>
            Remove
          </a>
        )}
      </div>
      <select
        value={value.priceId}
        onChange={(e) => {
          const plan = plans.find((p) => p.stripePriceId === e.target.value);
          // A plan with a required fee linked by an admin (see Product
          // Configuration) always brings that fee along -- there's no way
          // for the CSR to remove it here; only a coupon can discount it.
          const linkedFee = plan && plan.linkedFeePriceId
            ? fees.find((f) => f.stripePriceId === plan.linkedFeePriceId)
            : null;
          onChange({
            ...value,
            priceId: e.target.value,
            planTier: plan ? plan.displayName : '',
            initialFeePriceId: linkedFee ? linkedFee.stripePriceId : null,
            initialFeeCents: linkedFee ? linkedFee.unitAmountCents : 0,
            initialFeeDisplayName: linkedFee ? linkedFee.displayName : null,
            // If the new plan's required fee is one the CSR had separately
            // picked as an "extra" fee, drop it from extras so it isn't
            // double-counted -- it's now covered by the required-fee line.
            extraFeePriceIds: linkedFee
              ? value.extraFeePriceIds.filter((id) => id !== linkedFee.stripePriceId)
              : value.extraFeePriceIds,
          });
        }}
      >
        <option value="">Select a plan…</option>
        {plans.map((p) => (
          <option key={p.stripePriceId} value={p.stripePriceId}>
            {p.displayName} — {formatCents(p.unitAmountCents)}/mo
          </option>
        ))}
      </select>
      {showAllPlans && (
        <p className="hint-text" style={{ marginTop: 4 }}>Showing all plans, including specialized/less common ones.</p>
      )}
      {hideFees && (
        <p className="hint-text" style={{ marginTop: 4 }}>
          One-time fees aren't part of a plan change -- this only affects what bills going forward.
        </p>
      )}

      {addons.length > 0 && (
        <>
          <label>Add-ons</label>
          {addons.map((a) => (
            <label key={a.stripePriceId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--color-text)' }}>
              <input
                type="checkbox"
                style={{ width: 'auto', marginBottom: 0 }}
                checked={value.addonPriceIds.includes(a.stripePriceId)}
                onChange={() => toggleAddon(a.stripePriceId)}
              />
              {a.displayName} (+{formatCents(a.unitAmountCents)}/mo)
            </label>
          ))}
        </>
      )}

      {!hideFees && value.initialFeeDisplayName && (
        <>
          <label style={{ marginTop: 12 }}>Required one-time fee</label>
          <div style={{ fontSize: 14, color: 'var(--color-text)' }}>
            {value.initialFeeDisplayName} — {formatCents(value.initialFeeCents)}
          </div>
          <p className="hint-text" style={{ marginTop: 2 }}>Required with this plan. Only a coupon can discount it.</p>
        </>
      )}

      {!hideFees && extraFeeOptions.length > 0 && (
        <>
          <label style={{ marginTop: 12 }}>Additional one-time fees</label>
          {extraFeeOptions.map((f) => (
            <label key={f.stripePriceId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--color-text)' }}>
              <input
                type="checkbox"
                style={{ width: 'auto', marginBottom: 0 }}
                checked={value.extraFeePriceIds.includes(f.stripePriceId)}
                onChange={() => toggleExtraFee(f.stripePriceId)}
              />
              {f.displayName} (+{formatCents(f.unitAmountCents)} one-time)
            </label>
          ))}
          <p className="hint-text" style={{ marginTop: 2 }}>Add as many as apply — a trip fee, an extra key fee, whatever's needed for this sign-up.</p>
        </>
      )}

      <div style={{ fontSize: 14, marginTop: 8 }}>
        <strong>Monthly total: {formatCents(monthlyTotal)}</strong>
        {!hideFees && oneTimeTotal > 0 && (
          <span style={{ color: 'var(--color-text-muted)' }}> + {formatCents(oneTimeTotal)} one-time</span>
        )}
      </div>
    </div>
  );
}
